import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Crown, TrendingUp, Mail, MailOff, Heart, Trash2, Settings, History, BookOpen, Calculator, Gavel, Search, ChevronRight, ChevronDown, Loader2, ArrowLeft, DollarSign, Bookmark, BarChart3, Shield, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useTheme } from '../context/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIPDashboard = () => {
  const { sessionId } = useParams();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dailyEmailsEnabled, setDailyEmailsEnabled] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vip/dashboard/${sessionId}`);
      setDashboardData(res.data);
      setDailyEmailsEnabled(res.data.daily_emails_enabled);
    } catch {
      toast.error('Unable to load VIP dashboard');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const toggleDailyEmails = async () => {
    const newState = !dailyEmailsEnabled;
    try {
      await axios.put(`${API}/vip/toggle-daily-emails`, {
        session_id: sessionId,
        enabled: newState
      });
      setDailyEmailsEnabled(newState);
      toast.success(newState ? 'Daily emails resumed' : 'Daily emails paused');
    } catch {
      toast.error('Failed to update email preferences');
    }
  };

  const removeSavedRV = async (rvId) => {
    try {
      await axios.delete(`${API}/vip/saved-rv/${rvId}?session_id=${sessionId}`);
      setDashboardData(prev => ({
        ...prev,
        saved_rvs: prev.saved_rvs.filter(rv => rv.id !== rvId)
      }));
      toast.success('Removed from watchlist');
    } catch {
      toast.error('Failed to remove');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: isDark ? '#0a0a0f' : '#f8f9fa' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#F2A900' }} />
      </div>
    );
  }

  const bg = isDark ? '#0a0a0f' : '#f8f9fa';
  const cardBg = isDark ? '#141420' : '#fff';
  const border = isDark ? '#1e1e30' : '#e5e7eb';
  const textPrimary = isDark ? '#f0f0f5' : '#111827';
  const textSecondary = isDark ? '#8b8ba3' : '#6b7280';
  const accent = '#F2A900';
  const accentBg = isDark ? 'rgba(242,169,0,0.08)' : 'rgba(242,169,0,0.06)';

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Crown },
    { id: 'watchlist', label: 'Watchlist', icon: Heart },
    { id: 'tools', label: 'AI Tools', icon: Search },
    { id: 'preferences', label: 'Preferences', icon: Settings },
  ];

  return (
    <div data-testid="vip-dashboard" style={{ background: bg, minHeight: '100vh', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Header */}
      <div style={{ background: isDark ? '#101018' : '#fff', borderBottom: `1px solid ${border}`, padding: '16px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Link to={`/my-matches/${sessionId}`} style={{ color: textSecondary, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
              <ArrowLeft size={18} /> Back to Matches
            </Link>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Crown size={20} style={{ color: accent }} />
            <span style={{ color: accent, fontWeight: 700, fontSize: 16, letterSpacing: '-0.02em' }}>VIP Member</span>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
        {/* Welcome Banner */}
        <div data-testid="vip-welcome-banner" style={{
          background: `linear-gradient(135deg, ${isDark ? '#1a1a2e' : '#fff8e1'}, ${isDark ? '#16213e' : '#fff3cd'})`,
          border: `1px solid ${isDark ? 'rgba(242,169,0,0.2)' : 'rgba(242,169,0,0.3)'}`,
          borderRadius: 16, padding: '28px 32px', marginBottom: 24
        }}>
          <h1 style={{ color: textPrimary, fontSize: 28, fontWeight: 800, margin: 0, letterSpacing: '-0.03em' }}>
            Welcome back, {dashboardData?.lead?.name || 'VIP Member'}
          </h1>
          <p style={{ color: textSecondary, marginTop: 6, fontSize: 15 }}>
            Your personal RV intelligence hub. AI-powered matches, tools, and insights.
          </p>
          <div style={{ display: 'flex', gap: 24, marginTop: 16, flexWrap: 'wrap' }}>
            <StatBadge icon={<Mail size={15} />} label="Daily Emails" value={dailyEmailsEnabled ? 'Active' : 'Paused'} color={dailyEmailsEnabled ? '#22c55e' : '#ef4444'} />
            <StatBadge icon={<Heart size={15} />} label="Watchlist" value={`${dashboardData?.saved_rvs?.length || 0} RVs`} color={accent} />
            <StatBadge icon={<History size={15} />} label="Match Reports" value={`${dashboardData?.match_history?.length || 0} sent`} color="#6366f1" />
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, overflowX: 'auto', background: isDark ? '#111118' : '#f1f3f5', borderRadius: 12, padding: 4 }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-testid={`vip-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px',
                borderRadius: 8, border: 'none', cursor: 'pointer',
                background: activeTab === tab.id ? (isDark ? '#1e1e30' : '#fff') : 'transparent',
                color: activeTab === tab.id ? accent : textSecondary,
                fontWeight: activeTab === tab.id ? 600 : 400,
                fontSize: 14, transition: 'all 0.2s', whiteSpace: 'nowrap',
                boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && <OverviewTab data={dashboardData} isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary} accent={accent} accentBg={accentBg} sessionId={sessionId} />}
        {activeTab === 'watchlist' && <WatchlistTab savedRVs={dashboardData?.saved_rvs || []} onRemove={removeSavedRV} isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary} accent={accent} />}
        {activeTab === 'tools' && <AIToolsTab isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary} accent={accent} accentBg={accentBg} sessionId={sessionId} />}
        {activeTab === 'preferences' && <PreferencesTab data={dashboardData} sessionId={sessionId} dailyEmailsEnabled={dailyEmailsEnabled} onToggleEmails={toggleDailyEmails} isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary} accent={accent} onRefresh={fetchDashboard} />}
      </div>
    </div>
  );
};

// === Sub Components ===

const StatBadge = ({ icon, label, value, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ color }}>{icon}</div>
    <span style={{ color: '#8b8ba3', fontSize: 13 }}>{label}:</span>
    <span style={{ color, fontWeight: 600, fontSize: 13 }}>{value}</span>
  </div>
);

const OverviewTab = ({ data, isDark, cardBg, border, textPrimary, textSecondary, accent, accentBg, sessionId }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20 }}>
    {/* Quick Actions */}
    <div data-testid="vip-quick-actions" style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
      <h3 style={{ color: textPrimary, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Quick Actions</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <QuickAction icon={<Gavel size={18} />} title="AI Negotiation Coach" desc="Get a custom negotiation strategy" color="#6366f1" link={`/vip/${sessionId}/negotiation-coach`} />
        <QuickAction icon={<Search size={18} />} title="AI Deal Analyzer" desc="Is that RV a fair deal?" color="#ec4899" link={`/vip/${sessionId}/deal-analyzer`} />
        <QuickAction icon={<Calculator size={18} />} title="Cost Calculator" desc="True cost of ownership" color="#22c55e" link={`/vip/${sessionId}/cost-calculator`} />
        <QuickAction icon={<BookOpen size={18} />} title="RV Buying Guide" desc="Expert tips & strategies" color="#f59e0b" link={`/ebook/rv-buying-guide?token=${data?.membership?.id || 'dealer'}`} />
      </div>
    </div>

    {/* Recent Match History */}
    <div data-testid="vip-match-history" style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
      <h3 style={{ color: textPrimary, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Recent Match Reports</h3>
      {(!data?.match_history || data.match_history.length === 0) ? (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <History size={36} style={{ color: textSecondary, opacity: 0.3, margin: '0 auto' }} />
          <p style={{ color: textSecondary, marginTop: 12, fontSize: 14 }}>Your first daily match report will appear here tomorrow morning.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.match_history.slice(0, 5).map((match, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: 8, background: isDark ? '#1a1a28' : '#f9fafb' }}>
              <div>
                <p style={{ color: textPrimary, fontSize: 13, fontWeight: 500 }}>{match.matches_count || 0} matches found</p>
                <p style={{ color: textSecondary, fontSize: 12 }}>{match.sent_at ? new Date(match.sent_at).toLocaleDateString() : 'Pending'}</p>
              </div>
              <Check size={16} style={{ color: '#22c55e' }} />
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Saved RVs Preview */}
    <div data-testid="vip-saved-preview" style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24, gridColumn: 'span 2' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ color: textPrimary, fontSize: 16, fontWeight: 700 }}>Your Watchlist</h3>
        <span style={{ color: accent, fontSize: 13, fontWeight: 500 }}>{data?.saved_rvs?.length || 0} saved</span>
      </div>
      {(!data?.saved_rvs || data.saved_rvs.length === 0) ? (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <Heart size={36} style={{ color: textSecondary, opacity: 0.3, margin: '0 auto' }} />
          <p style={{ color: textSecondary, marginTop: 12, fontSize: 14 }}>Save RVs you're interested in to track their prices and get alerts.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 12 }}>
          {data.saved_rvs.slice(0, 4).map(rv => (
            <div key={rv.id} style={{ padding: 14, borderRadius: 10, border: `1px solid ${border}`, background: isDark ? '#1a1a28' : '#f9fafb' }}>
              <p style={{ color: textPrimary, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{rv.rv_name}</p>
              {rv.rv_price && <p style={{ color: accent, fontSize: 16, fontWeight: 700 }}>${rv.rv_price.toLocaleString()}</p>}
              <p style={{ color: textSecondary, fontSize: 12, marginTop: 4 }}>Saved {new Date(rv.saved_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

const QuickAction = ({ icon, title, desc, color, link }) => (
  <Link to={link} style={{ textDecoration: 'none' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 10, background: `${color}08`, border: `1px solid ${color}20`, cursor: 'pointer', transition: 'all 0.2s' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(4px)'; e.currentTarget.style.background = `${color}12`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateX(0)'; e.currentTarget.style.background = `${color}08`; }}
    >
      <div style={{ width: 38, height: 38, borderRadius: 10, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <p style={{ color: '#f0f0f5', fontSize: 14, fontWeight: 600, margin: 0 }}>{title}</p>
        <p style={{ color: '#8b8ba3', fontSize: 12, margin: 0 }}>{desc}</p>
      </div>
      <ChevronRight size={16} style={{ color: '#8b8ba3' }} />
    </div>
  </Link>
);

const WatchlistTab = ({ savedRVs, onRemove, isDark, cardBg, border, textPrimary, textSecondary, accent }) => (
  <div data-testid="vip-watchlist-tab">
    {savedRVs.length === 0 ? (
      <div style={{ textAlign: 'center', padding: '64px 0' }}>
        <Heart size={48} style={{ color: textSecondary, opacity: 0.3, margin: '0 auto' }} />
        <h3 style={{ color: textPrimary, marginTop: 16, fontSize: 18, fontWeight: 600 }}>Your watchlist is empty</h3>
        <p style={{ color: textSecondary, fontSize: 14, maxWidth: 400, margin: '8px auto 0' }}>
          When you find RVs you love, save them here to track prices and compare.
        </p>
      </div>
    ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {savedRVs.map(rv => (
          <div key={rv.id} data-testid={`watchlist-item-${rv.id}`} style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 20, transition: 'all 0.2s' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h4 style={{ color: textPrimary, fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{rv.rv_name}</h4>
                {rv.rv_brand && <p style={{ color: textSecondary, fontSize: 13 }}>{rv.rv_year} {rv.rv_brand} {rv.rv_model}</p>}
              </div>
              <button onClick={() => onRemove(rv.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', opacity: 0.6, padding: 4 }}
                onMouseEnter={e => e.currentTarget.style.opacity = 1}
                onMouseLeave={e => e.currentTarget.style.opacity = 0.6}>
                <Trash2 size={16} />
              </button>
            </div>
            {rv.rv_price && (
              <div style={{ marginTop: 12 }}>
                <p style={{ color: accent, fontSize: 22, fontWeight: 800 }}>${rv.rv_price.toLocaleString()}</p>
                {rv.price_history && rv.price_history.length > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                    <TrendingUp size={14} style={{ color: '#22c55e' }} />
                    <span style={{ color: '#22c55e', fontSize: 12, fontWeight: 500 }}>Price tracked</span>
                  </div>
                )}
              </div>
            )}
            {rv.notes && <p style={{ color: textSecondary, fontSize: 13, marginTop: 8, fontStyle: 'italic' }}>{rv.notes}</p>}
            <p style={{ color: textSecondary, fontSize: 12, marginTop: 10 }}>Saved {new Date(rv.saved_at).toLocaleDateString()}</p>
          </div>
        ))}
      </div>
    )}
  </div>
);

const AIToolsTab = ({ isDark, cardBg, border, textPrimary, textSecondary, accent, accentBg, sessionId }) => (
  <div data-testid="vip-tools-tab" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
    <ToolCard icon={<Gavel size={28} />} title="AI Negotiation Coach" desc="Paste any RV listing details and get a full negotiation playbook: opening offer, counter-offer scripts, red flags, and walk-away price." color="#6366f1" link={`/vip/${sessionId}/negotiation-coach`} tag="MOST POPULAR" isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary} />
    <ToolCard icon={<Search size={28} />} title="AI Deal Analyzer" desc="Is that RV a steal or a rip-off? Get an instant AI assessment with market comparison, depreciation forecast, and buy recommendation." color="#ec4899" link={`/vip/${sessionId}/deal-analyzer`} tag="NEW" isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary} />
    <ToolCard icon={<Calculator size={28} />} title="Total Cost Calculator" desc="See the REAL cost of RV ownership over 1-5 years including insurance, fuel, maintenance, storage, and depreciation." color="#22c55e" link={`/vip/${sessionId}/cost-calculator`} isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary} />
    <ToolCard icon={<BookOpen size={28} />} title="RV Buying Guide" desc="Your comprehensive guide covering everything from choosing the right class to financing, inspections, and negotiation strategies." color="#f59e0b" link={`/ebook/rv-buying-guide?token=dealer`} isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary} />
    <ToolCard icon={<BookOpen size={28} />} title="RV Travel Guide" desc="Plan your adventures with camping tips, route planning, essential gear lists, and expert travel advice from full-time RVers." color="#0ea5e9" link={`/ebook/rv-travel-guide?token=dealer`} isDark={isDark} cardBg={cardBg} border={border} textPrimary={textPrimary} textSecondary={textSecondary} />
  </div>
);

const ToolCard = ({ icon, title, desc, color, link, tag, isDark, cardBg, border, textPrimary, textSecondary }) => (
  <Link to={link} style={{ textDecoration: 'none' }}>
    <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 28, cursor: 'pointer', transition: 'all 0.25s', position: 'relative', overflow: 'hidden' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = `${color}40`; e.currentTarget.style.boxShadow = `0 8px 24px ${color}15`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = border; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {tag && <div style={{ position: 'absolute', top: 12, right: 12, background: `${color}20`, color, fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6, letterSpacing: '0.05em' }}>{tag}</div>}
      <div style={{ width: 52, height: 52, borderRadius: 14, background: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, marginBottom: 16 }}>{icon}</div>
      <h3 style={{ color: textPrimary, fontSize: 17, fontWeight: 700, marginBottom: 8 }}>{title}</h3>
      <p style={{ color: textSecondary, fontSize: 13, lineHeight: 1.6 }}>{desc}</p>
    </div>
  </Link>
);

const PreferencesTab = ({ data, sessionId, dailyEmailsEnabled, onToggleEmails, isDark, cardBg, border, textPrimary, textSecondary, accent, onRefresh }) => {
  const criteria = data?.preferences || {};
  const [budgetMax, setBudgetMax] = useState(criteria.budget_max || '');
  const [rvType, setRvType] = useState(criteria.rv_type || '');
  const [sleepCapacity, setSleepCapacity] = useState(criteria.sleep_capacity || '');
  const [saving, setSaving] = useState(false);

  const savePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/vip/preferences`, {
        session_id: sessionId,
        budget_max: budgetMax ? parseInt(budgetMax) : null,
        rv_type: rvType || null,
        sleep_capacity: sleepCapacity ? parseInt(sleepCapacity) : null,
      });
      toast.success('Preferences updated! Your next daily matches will use these.');
      onRefresh();
    } catch {
      toast.error('Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="vip-preferences-tab" style={{ maxWidth: 600 }}>
      {/* Daily Email Toggle */}
      <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ color: textPrimary, fontSize: 16, fontWeight: 700 }}>Daily Match Emails</h3>
            <p style={{ color: textSecondary, fontSize: 13, marginTop: 4 }}>Receive AI-curated RV matches in your inbox every morning.</p>
          </div>
          <button
            data-testid="toggle-daily-emails"
            onClick={onToggleEmails}
            style={{
              width: 52, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer',
              background: dailyEmailsEnabled ? '#22c55e' : (isDark ? '#333' : '#d1d5db'),
              position: 'relative', transition: 'all 0.3s'
            }}
          >
            <div style={{
              width: 22, height: 22, borderRadius: '50%', background: '#fff',
              position: 'absolute', top: 3,
              left: dailyEmailsEnabled ? 27 : 3,
              transition: 'left 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
            }} />
          </button>
        </div>
      </div>

      {/* Search Preferences */}
      <div style={{ background: cardBg, borderRadius: 14, border: `1px solid ${border}`, padding: 24 }}>
        <h3 style={{ color: textPrimary, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Search Preferences</h3>
        <p style={{ color: textSecondary, fontSize: 13, marginBottom: 20 }}>Update your criteria to get better daily matches.</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <Label style={{ color: textSecondary, fontSize: 13, marginBottom: 6, display: 'block' }}>Maximum Budget</Label>
            <Input
              data-testid="pref-budget"
              type="number"
              value={budgetMax}
              onChange={e => setBudgetMax(e.target.value)}
              placeholder="e.g. 75000"
              style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }}
            />
          </div>
          <div>
            <Label style={{ color: textSecondary, fontSize: 13, marginBottom: 6, display: 'block' }}>RV Type</Label>
            <select
              data-testid="pref-rv-type"
              value={rvType}
              onChange={e => setRvType(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: `1px solid ${border}`, background: isDark ? '#1a1a28' : '#f9fafb', color: textPrimary, fontSize: 14 }}
            >
              <option value="">Any Type</option>
              <option value="class_a">Class A Motorhome</option>
              <option value="class_b">Class B (Camper Van)</option>
              <option value="class_c">Class C Motorhome</option>
              <option value="travel_trailer">Travel Trailer</option>
              <option value="fifth_wheel">Fifth Wheel</option>
            </select>
          </div>
          <div>
            <Label style={{ color: textSecondary, fontSize: 13, marginBottom: 6, display: 'block' }}>Sleep Capacity</Label>
            <Input
              data-testid="pref-sleep"
              type="number"
              value={sleepCapacity}
              onChange={e => setSleepCapacity(e.target.value)}
              placeholder="e.g. 4"
              style={{ background: isDark ? '#1a1a28' : '#f9fafb', borderColor: border, color: textPrimary }}
            />
          </div>
          <Button
            data-testid="save-preferences-btn"
            onClick={savePreferences}
            disabled={saving}
            style={{ background: accent, color: '#000', fontWeight: 600, marginTop: 8, border: 'none' }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Preferences'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VIPDashboard;
